import { motion } from "framer-motion";

const TASTINGS = [
    {
        name: "Independent Tasting",
        price: "$30",
        tagline: "An intimate introduction",
        image:
            "https://static.wixstatic.com/media/d92b8c_aff76ef67204473c88043044dbb05ed8~mv2.png/v1/fill/w_1200,h_800,al_c,q_85,enc_auto/independent.png",
        includes: [
            "López Hill House & Van Gogh Gallery tour",
            "Slideshow on the creator and history of Atanasio",
            "Atanasio selection: Blanco · Reposado · Añejo",
            "Fresh fruit with Tajín",
        ],
        href: "https://www.lopezhill.com/service-page/independent-tasting",
    },
    {
        name: "Noble Tasting",
        price: "$40",
        tagline: "Most popular",
        featured: true,
        image:
            "https://static.wixstatic.com/media/d92b8c_244ac9d85a7f4593a937ce266e27a919~mv2.jpg/v1/fill/w_1200,h_800,al_c,q_85,enc_auto/noble.jpg",
        includes: [
            "House & Van Gogh Gallery tour",
            "Atanasio history & maker slideshow",
            "Atanasio Blanco · Reposado · Añejo",
            "Fresh fruit with Tajín",
            "Three artisan cheesecakes",
        ],
        href: "https://www.lopezhill.com/service-page/noble-tasting",
    },
    {
        name: "Royal Tasting",
        price: "$50",
        tagline: "The complete experience",
        image:
            "https://static.wixstatic.com/media/d92b8c_a270afa385e546cfa0fb65d71d2c4dce~mv2.jpg/v1/fill/w_1200,h_800,al_c,q_85,enc_auto/royal.jpg",
        includes: [
            "House & Van Gogh Gallery tour",
            "Atanasio history & maker slideshow",
            "Atanasio Blanco · Reposado · Añejo",
            "Cheese, olive & dry salami board (spicy · black pepper · sweet)",
            "Fresh fruit with Tajín",
            "Three artisan cheesecakes",
        ],
        href: "https://www.lopezhill.com/service-page/royal-tasting",
    },
];

export default function TequilaTastings() {
    return (
        <section
            id="tequila"
            className="section-py container-px bg-background"
            data-testid="tequila-section"
        >
            <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-end mb-16">
                <div className="lg:col-span-7">
                    <p className="overline">Atanasio Tequila Tastings</p>
                    <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05]">
                        From the fields to <em className="italic text-[hsl(var(--primary))]">our table.</em>
                    </h2>
                </div>
                <p className="lg:col-span-5 text-muted-foreground text-lg font-light leading-relaxed">
                    Immerse yourself in the aromas, history and craftsmanship of Atanasio —
                    a single tequila house we proudly pour at the hacienda. Three curated
                    tastings, all paired with a guided tour of the Van Gogh gallery.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border border border-border">
                {TASTINGS.map((t, i) => (
                    <motion.article
                        key={t.name}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: i * 0.08 }}
                        viewport={{ once: true, margin: "-50px" }}
                        className={`relative bg-background flex flex-col overflow-hidden ${
                            t.featured ? "md:scale-[1.02] md:-my-2 md:z-10 bg-[hsl(var(--muted))]" : ""
                        }`}
                        data-testid={`tasting-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                        {t.image && (
                            <div className="relative overflow-hidden">
                                <img
                                    src={t.image}
                                    alt={t.name}
                                    className="w-full h-48 object-cover transition-transform duration-[1400ms] hover:scale-[1.05]"
                                />
                            </div>
                        )}

                        <div className="p-8 sm:p-10 flex flex-col flex-1">
                            {t.featured && (
                                <span className="absolute top-4 right-4 bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] text-[10px] uppercase tracking-[0.25em] px-3 py-1">
                                    {t.tagline}
                                </span>
                            )}
                            {!t.featured && (
                                <p className="overline text-[10px]">{t.tagline}</p>
                            )}

                            <h3 className="mt-4 font-serif text-3xl tracking-tight">{t.name}</h3>

                            <div className="mt-6 flex items-baseline gap-2">
                                <span className="font-serif text-6xl font-light text-[hsl(var(--primary))]">
                                    {t.price}
                                </span>
                                <span className="overline text-[10px]">per guest</span>
                            </div>

                            <ul className="mt-8 space-y-3 text-sm font-light text-muted-foreground leading-relaxed flex-1">
                                {t.includes.map((line) => (
                                    <li key={line} className="flex gap-3">
                                        <span className="text-[hsl(var(--accent))] font-serif italic leading-none">&mdash;</span>
                                        <span>{line}</span>
                                    </li>
                                ))}
                            </ul>

                            <a
                                href="#booking"
                                className={`mt-10 inline-block text-center px-6 py-3 text-xs tracking-[0.25em] uppercase transition-all duration-300 ${
                                    t.featured
                                        ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary-hover))]"
                                        : "border border-[hsl(var(--primary))] text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))] hover:text-[hsl(var(--primary-foreground))]"
                                }`}
                                data-testid={`book-${t.name.toLowerCase().replace(/\s+/g, "-")}-btn`}
                            >
                                Reserve {t.name.split(" ")[0]}
                            </a>
                        </div>
                    </motion.article>
                ))}
            </div>

            <p className="mt-10 text-xs uppercase tracking-[0.25em] text-muted-foreground text-center">
                Tastings hosted Thursday–Saturday · By appointment Monday–Wednesday
            </p>
        </section>
    );
}
